import { createFileRoute } from "@tanstack/react-router";
import { Plus, Edit2, Trash2, Loader2, Tags } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API_BASE_URL } from "@/lib/api";

export const Route = createFileRoute("/admin/categories")({
  head: () => ({
    meta: [
      { title: "Manage Categories — Royal Lumina" },
    ],
  }),
  component: AdminCategories,
});

type FormCategory = {
  id?: string;
  name: string;
  description: string;
};

const emptyForm: FormCategory = {
  name: "",
  description: "",
};

const staticCategoriesData = [
  { id: "cat-1", name: "Luxury", slug: "luxury", description: "Luxury hand-poured candles for quiet luxury.", _count: { products: 3 } },
  { id: "cat-2", name: "Artisan", slug: "artisan", description: "Artisan poured candles with detailed sculptures.", _count: { products: 2 } },
  { id: "cat-3", name: "Decorative", slug: "decorative", description: "Decorative pieces designed for home rituals.", _count: { products: 3 } },
  { id: "cat-4", name: "Aroma", slug: "aroma", description: "Aromatherapy and therapeutic oil blends.", _count: { products: 3 } },
  { id: "cat-5", name: "Gift Sets", slug: "gift-sets", description: "Perfect gifts curated for special moments.", _count: { products: 1 } },
];

function AdminCategories() {
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<FormCategory>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      } else {
        setCategories(staticCategoriesData);
      }
    } catch (err) {
      setCategories(staticCategoriesData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleOpenCreate = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (c: any) => {
    setFormData({
      name: c.name,
      description: c.description || "",
    });
    setEditingId(c.id);
    setIsDialogOpen(true);
  };

  const handleDelete = async (category: any) => {
    const productCount = category._count?.products || 0;
    let message = `Are you sure you want to delete the category "${category.name}"?`;
    if (productCount > 0) {
      message += `\n\nWarning: There are ${productCount} product(s) linked to this category. They will be marked as "Uncategorized".`;
    }

    if (!confirm(message)) return;

    try {
      const token = localStorage.getItem("supabase.auth.token") || "";
      const res = await fetch(`${API_BASE_URL}/api/categories/${category.id}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (res.ok) {
        toast.success("Category deleted successfully");
        fetchCategories();
      } else {
        setCategories((prev) => prev.filter((c) => c.id !== category.id));
        toast.success("Category removed (Local state)");
      }
    } catch (err) {
      setCategories((prev) => prev.filter((c) => c.id !== category.id));
      toast.success("Category removed (Local state fallback)");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error("Please enter a category name");
      return;
    }

    try {
      const token = localStorage.getItem("supabase.auth.token") || "";
      const url = editingId 
        ? `${API_BASE_URL}/api/categories/${editingId}`
        : `${API_BASE_URL}/api/categories`;
      
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(editingId ? "Category updated successfully" : "Category added successfully");
        setIsDialogOpen(false);
        fetchCategories();
      } else {
        const errorData = await res.json();
        throw new Error(errorData.message || "API call failed");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save category");
      
      // Local state simulation
      if (editingId) {
        setCategories((prev) => 
          prev.map((c) => c.id === editingId ? { ...c, ...formData, slug: formData.name.toLowerCase().replace(/\s+/g, '-') } : c)
        );
        toast.success("Category updated (Local simulation)");
      } else {
        const newCat = {
          ...formData,
          id: `cat_${Date.now()}`,
          slug: formData.name.toLowerCase().replace(/\s+/g, '-'),
          _count: { products: 0 }
        };
        setCategories((prev) => [...prev, newCat]);
        toast.success("Category added (Local simulation)");
      }
      setIsDialogOpen(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-display text-4xl tracking-tight text-primary">Category Management</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage the collections and categories of your candles.</p>
        </div>
        <button
          onClick={handleOpenCreate}
          className="btn-gold self-start sm:self-auto"
        >
          <Plus className="h-4 w-4" />
          <span>Add Category</span>
        </button>
      </div>

      {/* Categories Table */}
      {loading ? (
        <div className="h-64 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="bg-card border border-border rounded-[24px] overflow-hidden shadow-luxe">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-muted/40 border-b border-border text-[0.65rem] uppercase tracking-wider text-muted-foreground font-semibold">
                  <th className="px-6 py-4">Category Details</th>
                  <th className="px-6 py-4">Slug</th>
                  <th className="px-6 py-4">Description</th>
                  <th className="px-6 py-4 text-center">Linked Products</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {categories.map((c) => (
                  <tr key={c.id} className="hover:bg-muted/10 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg bg-gold/10 flex items-center justify-center text-gold">
                          <Tags className="h-4 w-4" />
                        </div>
                        <div className="font-semibold text-primary">{c.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs text-muted-foreground">/{c.slug}</td>
                    <td className="px-6 py-4 text-muted-foreground max-w-xs truncate" title={c.description}>
                      {c.description || <span className="italic text-muted-foreground/50">No description</span>}
                    </td>
                    <td className="px-6 py-4 text-center font-semibold text-primary">
                      {c._count?.products || 0}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleOpenEdit(c)}
                          className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                          title="Edit Category"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(c)}
                          className="p-2 rounded-lg hover:bg-rose-50 text-muted-foreground hover:text-rose-600 transition-colors"
                          title="Delete Category"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit Dialog */}
      {isDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-primary/40 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-[24px] max-w-lg w-full p-8 shadow-luxe relative overflow-y-auto max-h-[90vh]">
            <h3 className="font-display text-2xl text-primary mb-6">
              {editingId ? "Edit Category Details" : "Create New Category"}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              <label className="block">
                <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Category Name</span>
                <input
                  type="text"
                  required
                  placeholder="e.g. Premium Blends"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                />
              </label>

              <label className="block">
                <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Description</span>
                <textarea
                  placeholder="Describe the collection..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-2 w-full h-24 p-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold resize-none"
                />
              </label>

              <div className="mt-8 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsDialogOpen(false)}
                  className="btn-outline-dark h-11 py-0 px-5 text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-gold h-11 py-0 px-6 text-xs"
                >
                  Save Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
